/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Returns a string to render into an `aria-live="polite"` region — empty on
 * the first render so AT doesn't read the initial state as a "change", then
 * updates whenever the watched value changes.
 *
 * Shared by Directory's count + page live regions so each announcement type
 * doesn't grow its own copy of the boilerplate.
 */
export function useChangeAnnouncement<V>(value: V, format: (v: V) => string): string {
	const [announcement, setAnnouncement] = useState("");
	const lastRef = useRef<V | null>(null);
	useEffect(() => {
		if (lastRef.current === null) {
			lastRef.current = value;
			return;
		}
		if (lastRef.current !== value) {
			lastRef.current = value;
			setAnnouncement(format(value));
		}
	}, [value, format]);
	return announcement;
}
